import React, { useState, useRef } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ArrowRight, ChevronLeft, ChevronRight, X, ArrowUpRight } from "lucide-react";
import { useModals } from "@/lib/modal-context";
import { openWhatsApp } from "@/lib/whatsapp";

const WhatsIcon = ({ className = "w-4 h-4" }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
        <path d="M19.05 4.91A9.83 9.83 0 0 0 12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.74.46 3.45 1.32 4.95L2 22l5.25-1.38a9.86 9.86 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.91-7.02Z"/>
    </svg>
);

const TABS = [
    { id: "all", label: "All" },
    { id: "offices", label: "Offices" },
    { id: "homes", label: "Homes" },
    { id: "retail", label: "Retail" },
    { id: "hospitality", label: "Hospitality" },
];

const PROJECTS = [
    {
        id: "exec-workspace",
        category: "offices", categoryLabel: "Office Fit-Out",
        title: "Executive Workspace Concept",
        desc: "Modern office setup designed for focus, flow and first impressions.",
        chips: ["Reception", "Workstations", "Meeting Room", "Partitions"],
        story: "A modern workspace direction created for companies that want a clean, professional and move-in-ready office with strong first impressions.",
        scope: ["Reception area", "Workstations", "Meeting room", "Partitions", "Furniture direction", "Finishes"],
        timeline: "45-day execution target, subject to scope, approvals and site condition.",
        estimateCategory: "office",
        cover: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1400&q=85",
        gallery: [
            "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1400&q=85",
            "https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=1400&q=85",
            "https://images.unsplash.com/photo-1600508774634-4e11d34730e2?auto=format&fit=crop&w=1400&q=85",
            "https://images.unsplash.com/photo-1497215842964-222b430dc094?auto=format&fit=crop&w=1400&q=85",
        ],
    },
    {
        id: "office-reception",
        category: "offices", categoryLabel: "Office Fit-Out",
        title: "Premium Office Reception",
        desc: "A clean reception area designed to create trust from the first step inside.",
        chips: ["Reception", "Feature Wall", "Lighting", "Finishes"],
        story: "A refined reception concept combining branded surfaces, warm lighting and durable finishes to build trust from the first impression.",
        scope: ["Reception counter", "Logo wall / signage", "Feature lighting", "Seating & waiting area", "Finishes & materials"],
        timeline: "45-day execution target, subject to scope, approvals and site condition.",
        estimateCategory: "office",
        cover: "https://images.unsplash.com/photo-1497215842964-222b430dc094?auto=format&fit=crop&w=1400&q=85",
        gallery: [
            "https://images.unsplash.com/photo-1497215842964-222b430dc094?auto=format&fit=crop&w=1400&q=85",
            "https://images.unsplash.com/photo-1600508774634-4e11d34730e2?auto=format&fit=crop&w=1400&q=85",
            "https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=1400&q=85",
            "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1400&q=85",
        ],
    },
    {
        id: "growth-workspace",
        category: "offices", categoryLabel: "Office Fit-Out",
        title: "Growth Team Workspace",
        desc: "A practical workspace layout for growing teams needing desks, meeting space and clean movement.",
        chips: ["Open Workspace", "Meeting Room", "Storage", "Furniture"],
        story: "An efficient layout for growing teams — balancing focus zones, huddle areas and clean circulation for day-to-day productivity.",
        scope: ["Open desking", "Enclosed meeting room", "Storage & lockers", "Furniture direction", "Lighting"],
        timeline: "45-day execution target, subject to scope, approvals and site condition.",
        estimateCategory: "office",
        cover: "https://images.unsplash.com/photo-1600508774634-4e11d34730e2?auto=format&fit=crop&w=1400&q=85",
        gallery: [
            "https://images.unsplash.com/photo-1600508774634-4e11d34730e2?auto=format&fit=crop&w=1400&q=85",
            "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1400&q=85",
            "https://images.unsplash.com/photo-1497215842964-222b430dc094?auto=format&fit=crop&w=1400&q=85",
            "https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=1400&q=85",
        ],
    },
    {
        id: "modern-apt",
        category: "homes", categoryLabel: "Home Interiors",
        title: "Modern Apartment Refresh",
        desc: "Calm, premium home interior direction for apartments and villas.",
        chips: ["Living Room", "Furniture", "Lighting", "Styling"],
        story: "A clean, contemporary apartment direction focused on comfort, natural light and a sense of everyday luxury.",
        scope: ["Living room layout", "Furniture selection", "Lighting plan", "Paint & feature walls", "Final styling"],
        timeline: "45-day execution target, subject to scope, approvals and site condition.",
        estimateCategory: "home",
        cover: "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&w=1400&q=85",
        gallery: [
            "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&w=1400&q=85",
            "https://images.unsplash.com/photo-1600210492493-0946911123ea?auto=format&fit=crop&w=1400&q=85",
            "https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?auto=format&fit=crop&w=1400&q=85",
            "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?auto=format&fit=crop&w=1400&q=85",
        ],
    },
    {
        id: "warm-luxury-home",
        category: "homes", categoryLabel: "Home Interiors",
        title: "Warm Luxury Home Concept",
        desc: "A home interior direction focused on comfort, premium finishes and everyday living.",
        chips: ["Bedroom", "Living Area", "Textures", "Decor"],
        story: "A warm, tactile home concept combining textured materials, layered lighting and pieces you actually want to come home to.",
        scope: ["Master bedroom", "Living & dining", "Custom joinery", "Lighting & textiles", "Decor & styling"],
        timeline: "45-day execution target, subject to scope, approvals and site condition.",
        estimateCategory: "home",
        cover: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1400&q=85",
        gallery: [
            "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1400&q=85",
            "https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?auto=format&fit=crop&w=1400&q=85",
            "https://images.unsplash.com/photo-1631679706909-1844bbd07221?auto=format&fit=crop&w=1400&q=85",
            "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&w=1400&q=85",
        ],
    },
    {
        id: "premium-storefront",
        category: "retail", categoryLabel: "Retail Branding",
        title: "Premium Retail Storefront",
        desc: "A retail environment designed to improve visibility, customer flow and in-store experience.",
        chips: ["Storefront", "Display Zones", "Lighting", "Customer Flow"],
        story: "A confident retail environment with clear sightlines, considered lighting and a customer journey designed to convert.",
        scope: ["Storefront treatment", "Display fixtures", "Signage & branding", "Lighting design", "Customer flow"],
        timeline: "45-day execution target, subject to scope, approvals and site condition.",
        estimateCategory: "retail",
        cover: "https://images.unsplash.com/photo-1519415943484-9fa1873496d4?auto=format&fit=crop&w=1400&q=85",
        gallery: [
            "https://images.unsplash.com/photo-1519415943484-9fa1873496d4?auto=format&fit=crop&w=1400&q=85",
            "https://images.unsplash.com/photo-1441984904996-e0b6ba687e04?auto=format&fit=crop&w=1400&q=85",
            "https://images.unsplash.com/photo-1567401893414-76b7b1e5a7a5?auto=format&fit=crop&w=1400&q=85",
        ],
    },
    {
        id: "boutique-retail",
        category: "retail", categoryLabel: "Retail Branding",
        title: "Boutique Retail Interior",
        desc: "A clean retail layout designed for product display, customer movement and premium feel.",
        chips: ["Display", "Counters", "Lighting", "Finishes"],
        story: "A boutique interior built for product-forward display, elegant counters and a premium in-store feel.",
        scope: ["Display shelving", "Counter design", "Feature lighting", "Wall finishes", "Signage"],
        timeline: "45-day execution target, subject to scope, approvals and site condition.",
        estimateCategory: "retail",
        cover: "https://images.unsplash.com/photo-1441984904996-e0b6ba687e04?auto=format&fit=crop&w=1400&q=85",
        gallery: [
            "https://images.unsplash.com/photo-1441984904996-e0b6ba687e04?auto=format&fit=crop&w=1400&q=85",
            "https://images.unsplash.com/photo-1519415943484-9fa1873496d4?auto=format&fit=crop&w=1400&q=85",
            "https://images.unsplash.com/photo-1567401893414-76b7b1e5a7a5?auto=format&fit=crop&w=1400&q=85",
        ],
    },
    {
        id: "cafe-concept",
        category: "hospitality", categoryLabel: "Restaurant / Café",
        title: "Café Interior Concept",
        desc: "Warm café space with mood lighting, seating flow and memorable customer corners.",
        chips: ["Seating", "Counter", "Lighting", "Wall Finish"],
        story: "A warm café direction with soft mood lighting, comfortable seating flow and photo-friendly corners that guests want to come back to.",
        scope: ["Seating layout", "Counter & bar", "Mood lighting", "Wall finishes", "Menu boards"],
        timeline: "45-day execution target, subject to scope, approvals and site condition.",
        estimateCategory: "restaurant",
        cover: "https://images.unsplash.com/photo-1554118811-1e0d58224f24?auto=format&fit=crop&w=1400&q=85",
        gallery: [
            "https://images.unsplash.com/photo-1554118811-1e0d58224f24?auto=format&fit=crop&w=1400&q=85",
            "https://images.unsplash.com/photo-1445116572660-236099ec97a0?auto=format&fit=crop&w=1400&q=85",
            "https://images.unsplash.com/photo-1552566626-52f8b828add9?auto=format&fit=crop&w=1400&q=85",
        ],
    },
    {
        id: "restaurant-dining",
        category: "hospitality", categoryLabel: "Restaurant / Café",
        title: "Restaurant Dining Experience",
        desc: "A dining space direction built around comfort, atmosphere and brand recall.",
        chips: ["Dining Layout", "Mood Lighting", "Seating", "Materials"],
        story: "A dining concept built for atmosphere — layered lighting, considered seating and materials that shape a memorable brand experience.",
        scope: ["Dining layout", "Mood & feature lighting", "Seating design", "Wall & floor materials", "Signage & branding"],
        timeline: "45-day execution target, subject to scope, approvals and site condition.",
        estimateCategory: "restaurant",
        cover: "https://images.unsplash.com/photo-1552566626-52f8b828add9?auto=format&fit=crop&w=1400&q=85",
        gallery: [
            "https://images.unsplash.com/photo-1552566626-52f8b828add9?auto=format&fit=crop&w=1400&q=85",
            "https://images.unsplash.com/photo-1445116572660-236099ec97a0?auto=format&fit=crop&w=1400&q=85",
            "https://images.unsplash.com/photo-1554118811-1e0d58224f24?auto=format&fit=crop&w=1400&q=85",
        ],
    },
];

function ProjectCard({ p, onOpen }) {
    return (
        <div className="shrink-0 w-[85%] sm:w-[43%] lg:w-[28%] snap-start">
            <div className="rounded-3xl bg-white border border-black/10 card-lift overflow-hidden flex flex-col h-full">
                <div className="relative h-56 overflow-hidden">
                    <img src={p.cover} alt={p.title} className="h-full w-full object-cover" loading="lazy" />
                    <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/50 to-transparent" />
                    <span className="absolute top-3 left-3 rounded-full bg-white/90 backdrop-blur px-3 py-1 text-[10px] font-bold text-black uppercase tracking-wider">
                        {p.categoryLabel}
                    </span>
                </div>
                <div className="p-5 flex-1 flex flex-col">
                    <h3 className="font-display text-lg font-bold leading-tight">{p.title}</h3>
                    <p className="mt-2 text-[13px] text-black/60 leading-relaxed line-clamp-2">{p.desc}</p>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                        {p.chips.map((chip) => (
                            <span key={chip} className="inline-flex items-center rounded-full bg-[#FAFAF5] border border-black/10 px-2.5 py-1 text-[10px] font-semibold text-black/70">
                                {chip}
                            </span>
                        ))}
                    </div>
                    <button
                        onClick={() => onOpen(p)}
                        data-testid={`project-view-${p.id}`}
                        className="mt-5 w-full btn-pill btn-outline-dark justify-between pr-1 py-1.5"
                    >
                        <span className="text-[12px] font-bold tracking-wider">VIEW PROJECT</span>
                        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#CCFF00] text-black">
                            <ArrowUpRight className="h-3.5 w-3.5" />
                        </span>
                    </button>
                </div>
            </div>
        </div>
    );
}

function ProjectModal({ project, onClose, onEstimate }) {
    const [imgIdx, setImgIdx] = useState(0);
    if (!project) return null;
    const gallery = project.gallery || [project.cover];
    const prev = () => setImgIdx((i) => (i - 1 + gallery.length) % gallery.length);
    const next = () => setImgIdx((i) => (i + 1) % gallery.length);

    return (
        <Dialog open={!!project} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="sm:max-w-[1100px] p-0 overflow-hidden rounded-3xl border-0 max-h-[92vh] overflow-y-auto bg-white" data-testid="project-modal">
                <div className="grid grid-cols-1 lg:grid-cols-2">
                    {/* Left — Gallery */}
                    <div className="relative bg-black min-h-[280px] lg:min-h-[560px]">
                        <img src={gallery[imgIdx]} alt={project.title} className="absolute inset-0 h-full w-full object-cover" />
                        {gallery.length > 1 && (
                            <>
                                <button onClick={prev} aria-label="prev image" className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 hover:bg-white flex items-center justify-center">
                                    <ChevronLeft className="h-5 w-5" />
                                </button>
                                <button onClick={next} aria-label="next image" className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 hover:bg-white flex items-center justify-center">
                                    <ChevronRight className="h-5 w-5" />
                                </button>
                                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                                    {gallery.map((_, i) => (
                                        <button
                                            key={i}
                                            onClick={() => setImgIdx(i)}
                                            aria-label={`image ${i + 1}`}
                                            className={`h-1.5 rounded-full transition-all ${i === imgIdx ? "w-6 bg-[#CCFF00]" : "w-1.5 bg-white/60"}`}
                                        />
                                    ))}
                                </div>
                            </>
                        )}
                    </div>

                    {/* Right — Details */}
                    <div className="p-6 sm:p-8 flex flex-col">
                        <button
                            onClick={onClose}
                            aria-label="close"
                            className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-[#CCFF00] text-black hover:scale-105 transition"
                            data-testid="project-modal-close"
                        >
                            <X className="h-4 w-4" />
                        </button>
                        <span className="inline-flex self-start items-center rounded-full bg-[#FFFDE8] border border-[#CCFF00]/40 px-3 py-1 text-[10px] font-bold text-[#5d6d00] uppercase tracking-wider">
                            {project.categoryLabel}
                        </span>
                        <h3 className="mt-3 font-display text-2xl sm:text-[28px] font-extrabold tracking-[-0.02em] leading-tight">{project.title}</h3>
                        <p className="mt-3 text-sm text-black/65 leading-relaxed">{project.story}</p>

                        <div className="mt-5">
                            <div className="text-[11px] font-bold tracking-wider text-black/50">SCOPE INCLUDED</div>
                            <ul className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5">
                                {project.scope.map((s) => (
                                    <li key={s} className="text-[13px] text-black/75 flex items-start gap-2">
                                        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#7e9a00] shrink-0" />
                                        {s}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="mt-5">
                            <div className="text-[11px] font-bold tracking-wider text-black/50">TIMELINE</div>
                            <div className="mt-1.5 text-[13px] text-black/75">{project.timeline}</div>
                        </div>

                        <div className="mt-6 flex flex-col sm:flex-row gap-2.5 sm:sticky sm:bottom-0 bg-white pt-2">
                            <button
                                onClick={() => onEstimate(project)}
                                className="btn-pill btn-lime pr-1 flex-1 justify-between"
                                data-testid="project-modal-estimate"
                            >
                                <span className="text-[12px] font-bold tracking-wider pl-2">GET SIMILAR ESTIMATE</span>
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-black text-[#CCFF00]"><ArrowRight className="h-3.5 w-3.5" /></span>
                            </button>
                            <button
                                onClick={() => openWhatsApp(`Hi! I'd like to discuss a project similar to your "${project.title}" concept (${project.categoryLabel}).`)}
                                className="btn-pill btn-outline-dark pr-1 flex-1 justify-between"
                                data-testid="project-modal-whatsapp"
                            >
                                <span className="text-[12px] font-bold tracking-wider pl-2">WHATSAPP US</span>
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#25D366] text-white"><WhatsIcon className="h-3.5 w-3.5" /></span>
                            </button>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default function ProjectCatalogue() {
    const { openConnect, openEstimator } = useModals();
    const [active, setActive] = useState("all");
    const [open, setOpen] = useState(null);
    const scrollRef = useRef(null);

    const filtered = active === "all" ? PROJECTS : PROJECTS.filter((p) => p.category === active);

    const scrollBy = (dir) => {
        if (!scrollRef.current) return;
        const w = scrollRef.current.clientWidth;
        scrollRef.current.scrollBy({ left: dir * (w * 0.6), behavior: "smooth" });
    };

    return (
        <section id="projects" className="bg-white py-16 sm:py-20">
            <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end fade-up">
                    <div className="lg:col-span-8">
                        <div className="text-[#7e9a00] text-xs font-bold tracking-[0.22em]">PROJECT CATALOGUE</div>
                        <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-[52px] font-extrabold leading-[1.05] tracking-[-0.03em]">
                            Selected Work &amp; Space Concepts
                        </h2>
                        <p className="mt-3 text-sm sm:text-[15px] text-black/60 max-w-2xl leading-relaxed">
                            Explore related work, space concepts and execution experience across offices, homes, retail and hospitality environments.
                        </p>
                    </div>
                    <div className="lg:col-span-4">
                        <p className="text-[12px] text-black/55 leading-relaxed lg:text-right italic">
                            Built by a team experienced in fast project coordination, interiors, fit-out and premium space execution across the UAE.
                        </p>
                    </div>
                </div>

                {/* Tabs + arrows */}
                <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex flex-wrap gap-2" data-testid="catalogue-tabs">
                        {TABS.map((t) => (
                            <button
                                key={t.id}
                                onClick={() => setActive(t.id)}
                                data-testid={`cat-tab-${t.id}`}
                                className={`px-4 py-2 rounded-full text-[12px] font-bold border transition-all ${
                                    active === t.id
                                        ? "bg-[#CCFF00] border-[#CCFF00] text-black"
                                        : "bg-white border-black/10 text-black/70 hover:border-black/30"
                                }`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <button onClick={() => scrollBy(-1)} aria-label="scroll left" className="h-10 w-10 rounded-full border border-black/10 bg-white hover:bg-[#CCFF00] flex items-center justify-center transition">
                            <ChevronLeft className="h-4 w-4" />
                        </button>
                        <button onClick={() => scrollBy(1)} aria-label="scroll right" className="h-10 w-10 rounded-full border border-black/10 bg-white hover:bg-[#CCFF00] flex items-center justify-center transition">
                            <ChevronRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>

                {/* Horizontal carousel */}
                <div
                    ref={scrollRef}
                    className="mt-6 flex gap-4 overflow-x-auto snap-x snap-mandatory no-scrollbar pb-2 -mx-4 px-4"
                    data-testid="catalogue-scroll"
                >
                    {filtered.map((p) => (
                        <ProjectCard key={p.id} p={p} onOpen={setOpen} />
                    ))}
                </div>

                {/* Bottom compact CTA strip */}
                <div className="mt-10 rounded-3xl bg-[#111] p-6 sm:p-8 text-white">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-center">
                        <div className="lg:col-span-7">
                            <h3 className="font-display text-2xl sm:text-3xl font-extrabold tracking-[-0.02em]">
                                Have a space to build?
                            </h3>
                            <p className="mt-2 text-sm text-white/70 max-w-2xl leading-relaxed">
                                Share your space type, size, location and timeline. We&apos;ll help you understand the right package, approximate budget and 45-day execution feasibility.
                            </p>
                        </div>
                        <div className="lg:col-span-5 flex flex-col sm:flex-row lg:justify-end gap-2.5">
                            <button
                                onClick={() => openEstimator("office")}
                                data-testid="catalogue-cta-estimate"
                                className="btn-pill btn-lime pr-1"
                            >
                                <span className="text-[12px] font-bold tracking-wider pl-2">GET FREE SPACE ESTIMATE</span>
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-black text-[#CCFF00]"><ArrowRight className="h-3.5 w-3.5" /></span>
                            </button>
                            <button
                                onClick={() => openConnect({ source: "catalogue_cta" })}
                                data-testid="catalogue-cta-whatsapp"
                                className="btn-pill bg-white text-black pr-1"
                            >
                                <span className="text-[12px] font-bold tracking-wider pl-2">WHATSAPP US IN 15 SECS</span>
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#25D366] text-white"><WhatsIcon className="h-3.5 w-3.5" /></span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <ProjectModal
                project={open}
                onClose={() => setOpen(null)}
                onEstimate={(p) => {
                    setOpen(null);
                    setTimeout(() => openConnect({ source: `catalogue_${p.id}`, category: p.title }), 200);
                }}
            />
        </section>
    );
}
